// Import/Export and Dashboard Stats APIs
import { getApiClient, withOrgConfig, asArray } from '../quote-api-helpers'
import type {
  ImportTemplate, ImportRecord, ExportRecord, ImportResult,
  DashboardStats, DashboardActivity,
} from '../quote-api-reference-types'
import { quotesCrudApi } from './quotes-crud-api'

export const importDashboardApi = {
  // =========================================================================
  // IMPORT/EXPORT
  // =========================================================================

  async getImportTemplates(): Promise<ImportTemplate[]> {
    const client = getApiClient()
    const res = await client.get<ImportTemplate[]>(`/quotebuilder/import-templates/`, withOrgConfig())
    return asArray(res.data)
  },

  async getImportRecords(quoteId?: string): Promise<ImportRecord[]> {
    const client = getApiClient()
    const params: Record<string, string> = {}
    if (quoteId) params.quote_id = quoteId
    const res = await client.get<ImportRecord[]>(`/quotebuilder/import-records/`, withOrgConfig({ params }))
    return asArray(res.data)
  },

  async getExportRecords(quoteId?: string): Promise<ExportRecord[]> {
    const client = getApiClient()
    const params: Record<string, string> = {}
    if (quoteId) params.quote_id = quoteId
    const res = await client.get<ExportRecord[]>(`/quotebuilder/export-records/`, withOrgConfig({ params }))
    return asArray(res.data)
  },

  async importExcel(
    file: File,
    options: {
      importType: 'new' | 'revision' | 'variation' | 'daysheet'
      parentQuoteId?: string
      templateId?: string
      dryRun?: boolean
    }
  ): Promise<ImportResult> {
    const client = getApiClient()
    const formData = new FormData()
    formData.append('file', file)
    formData.append('import_type', options.importType)
    if (options.parentQuoteId) formData.append('parent_quote_id', options.parentQuoteId)
    if (options.templateId) formData.append('template_id', options.templateId)
    if (options.dryRun) formData.append('dry_run', 'true')

    const res = await client.post<ImportResult>(
      `/quotebuilder/import/excel/`,
      formData,
      withOrgConfig({
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    )
    return res.data
  },

  // =========================================================================
  // DASHBOARD STATS
  // =========================================================================

  // Dashboard stats - fetched from backend API with Simpro win-rate integration
  async getDashboardStats(period: 'week' | 'month' | 'quarter' | 'year' = 'month') {
    const client = getApiClient()
    try {
      const res = await client.get<DashboardStats>(
        `/quotebuilder/dashboard/stats/`,
        withOrgConfig({ params: { period } })
      )
      return res.data
    } catch (error) {
      // Fallback to client-side calculation if API fails
      console.warn('Dashboard API failed, falling back to client-side calculation', error)
      return importDashboardApi._fallbackDashboardStats()
    }
  },

  // Fallback client-side calculation if backend API fails
  async _fallbackDashboardStats() {
    const quotes = await quotesCrudApi.listQuotes()
    const now = new Date()
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    const startOfWeek = new Date(now)
    startOfWeek.setDate(now.getDate() - now.getDay())

    const thisMonthQuotes = quotes.filter((q) => {
      const d = new Date(q.created_at || q.quote_date || '')
      return d >= startOfMonth
    })
    const thisWeekQuotes = quotes.filter((q) => {
      const d = new Date(q.created_at || q.quote_date || '')
      return d >= startOfWeek
    })

    const toNum = (v: string | number | null | undefined): number => {
      if (typeof v === 'number') return v
      if (typeof v === 'string') return parseFloat(v) || 0
      return 0
    }

    const totalValue = thisMonthQuotes.reduce((sum, q) => sum + toNum(q.total_excl_gst), 0)
    const accepted = quotes.filter((q) => q.status === 'accepted')
    const acceptedThisMonth = thisMonthQuotes.filter((q) => q.status === 'accepted')
    const sentThisWeek = thisWeekQuotes.filter((q) => q.status === 'sent' || q.status === 'accepted' || q.status === 'rejected')
    const sentThisWeekValue = sentThisWeek.reduce((sum, q) => sum + toNum(q.total_excl_gst), 0)

    const drafts = quotes.filter((q) => q.status === 'draft')
    const pending = quotes.filter((q) => q.status === 'pending_approval' || q.status === 'pending_review')
    const sent = quotes.filter((q) => q.status === 'sent')

    const winRate = thisMonthQuotes.length > 0
      ? Math.round((acceptedThisMonth.length / thisMonthQuotes.length) * 100)
      : 0

    return {
      activeQuotes: drafts.length + pending.length + sent.length,
      activeQuotesChange: `${thisWeekQuotes.filter((q) => ['draft', 'pending_approval', 'sent'].includes(q.status)).length} this week`,
      pendingReview: pending.length,
      pendingReviewChange: pending.filter((q) => toNum(q.total_excl_gst) > 50000).length > 0 ? `${pending.filter((q) => toNum(q.total_excl_gst) > 50000).length} high value` : 'none urgent',
      acceptedTotal: accepted.length,
      acceptedChange: `+${acceptedThisMonth.length} this month`,
      sentThisWeek: sentThisWeek.length,
      sentThisWeekValue,
      quotesThisMonth: thisMonthQuotes.length,
      totalValueThisMonth: totalValue,
      winRate,
      winRateDetails: {
        won: acceptedThisMonth.length,
        lost: thisMonthQuotes.filter((q) => q.status === 'rejected').length,
        pending: thisMonthQuotes.filter((q) => !['accepted', 'rejected'].includes(q.status)).length,
        conversion_rate: 0,
      },
      avgQuoteValue: thisMonthQuotes.length > 0 ? Math.round(totalValue / thisMonthQuotes.length) : 0,
      recentQuotes: quotes.slice(0, 5),
      statusBreakdown: {
        draft: drafts.length,
        pending: pending.length,
        sent: sent.length,
        accepted: accepted.length,
        rejected: quotes.filter((q) => q.status === 'rejected').length,
      },
      trends: [],
    }
  },

  // Dashboard activity feed
  async getDashboardActivity(limit: number = 20) {
    const client = getApiClient()
    const res = await client.get<{ activities: DashboardActivity[] }>(
      `/quotebuilder/dashboard/activity/`,
      withOrgConfig({ params: { limit } })
    )
    return res.data.activities
  },
}
